// ── tool-view-sections: HONEST diagnostics blocks (FR-TOOL-001) ─────────────
// Renders the three secret-free diagnostics blocks the SA Tool View shows:
//   1. provider-gate status: per lane OPEN/CLOSED + live | dry_run mode;
//   2. outbox health: coaching_side_effect_outbox counts by status + provider mode;
//   3. config flags: the *_LIVE_ENABLED booleans + a configured / not-configured
//      indicator per secret-bearing var.
//
// NO SECRET VALUES are rendered. The per-secret status is a NEUTRAL badge only
// ("configured" / "not configured") — the value is never present in the payload
// and this UI never attempts to display one. Booleans/states/counts only.

import { Badge } from "@vjal/ui";

import type {
  CoachingToolViewDiagnosticsDto,
  CoachingToolViewOutboxHealthDto,
  CoachingToolViewProviderLaneDto
} from "../../services/coaching-readiness-dtos";

export function ToolViewProviderGates({ lanes }: { lanes: CoachingToolViewProviderLaneDto[] }) {
  return (
    <section data-testid="tool-view-gates" className="space-y-2">
      <h3 className="m-0 text-sm font-semibold text-ink">Provider gates</h3>
      <ul className="m-0 flex flex-col gap-2 p-0">
        {lanes.map((lane) => (
          <li
            key={lane.lane}
            data-testid={`gate-${lane.lane}`}
            className="flex items-center justify-between rounded-lg border border-line bg-surface px-3 py-2"
          >
            <span className="font-medium text-ink">{lane.lane}</span>
            <span className="flex items-center gap-2">
              <Badge variant={lane.open ? "success" : "warning"}>{lane.open ? "OPEN" : "CLOSED"}</Badge>
              <Badge variant={lane.mode === "live" ? "info" : "default"}>{lane.mode}</Badge>
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

export function ToolViewOutboxHealth({ outbox }: { outbox: CoachingToolViewOutboxHealthDto }) {
  return (
    <section data-testid="tool-view-outbox" className="space-y-2">
      <h3 className="m-0 text-sm font-semibold text-ink">Outbox health</h3>
      <p className="m-0 text-xs text-ink-muted">
        {outbox.table}: {outbox.total} total
      </p>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <CountGroup title="By status" counts={outbox.byStatus} testId="outbox-by-status" />
        <CountGroup title="By provider mode" counts={outbox.byProviderMode} testId="outbox-by-provider-mode" />
      </div>
    </section>
  );
}

function CountGroup({ title, counts, testId }: { title: string; counts: Record<string, number>; testId: string }) {
  const entries = Object.entries(counts);
  return (
    <div data-testid={testId} className="rounded-lg border border-line bg-surface p-3">
      <p className="m-0 mb-2 text-xs font-semibold uppercase tracking-wide text-ink-muted">{title}</p>
      <ul className="m-0 flex list-none flex-col gap-1 p-0">
        {entries.map(([key, value]) => (
          <li key={key} className="flex items-center justify-between text-sm">
            <span className="m-0 text-ink-muted">{key}</span>
            <span className="m-0 font-medium text-ink">{value}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function ToolViewConfigFlags({
  configFlags,
  secretsRedacted
}: {
  configFlags: CoachingToolViewDiagnosticsDto["configFlags"];
  secretsRedacted: true;
}) {
  const gateEntries = Object.entries(configFlags.gateBooleans);
  const secretEntries = Object.entries(configFlags.secretsConfigured);
  return (
    <section data-testid="tool-view-flags" className="space-y-3">
      <h3 className="m-0 text-sm font-semibold text-ink">Config flags</h3>

      <div data-testid="tool-view-gate-booleans" className="rounded-lg border border-line bg-surface p-3">
        <p className="m-0 mb-2 text-xs font-semibold uppercase tracking-wide text-ink-muted">Live / cron enable flags</p>
        <ul className="m-0 grid grid-cols-1 gap-1 p-0 sm:grid-cols-2">
          {gateEntries.map(([name, on]) => (
            <li key={name} className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-2 text-sm">
              <span
                className="min-w-0 overflow-x-auto whitespace-nowrap font-mono text-xs text-ink-muted focus-visible:outline-none focus-visible:shadow-focus"
                title={name}
                tabIndex={0}
                aria-label={`Config flag ${name}`}
              >
                {name}
              </span>
              <Badge variant={on ? "success" : "default"} className="shrink-0">{on ? "on" : "off"}</Badge>
            </li>
          ))}
        </ul>
      </div>

      {/* Per secret-bearing var: a NEUTRAL configured / not-configured badge ONLY.
          The VALUE is never present in the payload and is never shown here. */}
      <div data-testid="tool-view-secrets" className="rounded-lg border border-line bg-surface p-3">
        <p className="m-0 mb-2 text-xs font-semibold uppercase tracking-wide text-ink-muted">
          Secret-bearing vars (values redacted; configured status only)
        </p>
        <ul className="m-0 grid grid-cols-1 gap-1 p-0 sm:grid-cols-2">
          {secretEntries.map(([name, configured]) => (
            <li key={name} data-testid={`secret-${name}`} className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-2 text-sm">
              <span
                className="min-w-0 overflow-x-auto whitespace-nowrap font-mono text-xs text-ink-muted focus-visible:outline-none focus-visible:shadow-focus"
                title={name}
                tabIndex={0}
                aria-label={`Secret-bearing config key ${name}`}
              >
                {name}
              </span>
              <Badge variant="default" className="shrink-0">{configured ? "configured" : "not configured"}</Badge>
            </li>
          ))}
        </ul>
      </div>

      {secretsRedacted ? (
        <p className="m-0 text-xs text-ink-muted" data-testid="tool-view-secrets-redacted">
          The diagnostics response contains no secret values — only states, counts, and configured/not-configured
          booleans.
        </p>
      ) : null}
    </section>
  );
}
